<template>
    <div
        class="product-price-info"
    >
        <template
            v-if="hasDiscount"
        >
            <div
                class="d-flex align-center justify-start font-weight-medium"
            >
                <span
                    class="v-align-middle text-medium-emphasis text-decoration-line-through"
                    :class="[
                        fontSize,
                        {
                            'mr-3': !orderReverse
                        }
                    ]"
                >
                    {{ formatCurrency(prices.price) }}
                </span>
                <span class="d-sr-only">, is</span>
                <span
                    class="v-align-middle"
                    :class="[
                        fontSize,
                        {
                            'order-first mr-3': orderReverse
                        }
                    ]"
                >
                    {{ formatCurrency(prices.userPrice) }}
                </span>
            </div>
        </template>
        <template
            v-else
        >
            <span
                class="v-align-middle font-weight-medium"
                :class="[
                    fontSize
                ]"
            >
                {{ formatCurrency(prices.price) }}
            </span>
        </template>
    </div>
</template>

<script lang="ts" setup>
    import type {
        GenericObject
    } from '~/types';
    import { formatCurrency } from 'x1-base/utils';


    type Sizes = Nullable<'xs' | 'sm' | 'md' | 'lg'>;
    interface ObjectWithStatus extends GenericObject<any> {
        status: Nullable<string>;
    };
    interface ObjectWithPriceAugmentation extends GenericObject<any> {
        upcharge: number;
        userUpcharge: number;
    };

    const props = withDefaults(
        defineProps<{
            currentSku: ObjectWithStatus;
            augmentPrices?: Nilish<ObjectWithPriceAugmentation[]>;
            size?: Sizes;
            orderReverse?: boolean;
            noBold?: boolean;
        }>(),
        {
            size: 'md'
        }
    );

    const sizes: Nullable<Sizes>[] = [null, null, null, null, 'lg', 'md', 'sm', 'xs'];
    const prices = reactive({
        price: props.currentSku.price,
        userPrice: props.currentSku.userPrice
    });

    const hasDiscount = computed<boolean>(() => props.currentSku.userDiscount > 0);
    const fontSize = computed<string>(() => {
        let size: number = sizes.findIndex((s) => s === props.size) ?? 5;
        return `font-size-${size}`;
    });

    function setPrice(): void {
        const newPrices = props.augmentPrices;

        if (newPrices?.length) {
            const countedSkus: string[] = [];
            let priceUpcharge = 0;
            let priceUserUpcharge = 0;

            newPrices.forEach((obj) => {
                const { skuUid, upcharge, userUpcharge } = obj;

                if (!countedSkus.includes(skuUid)) {
                    priceUpcharge += (upcharge ?? 0);
                    priceUserUpcharge += (userUpcharge ?? 0);
                    countedSkus.push(skuUid);
                }
            });

            prices.price = props.currentSku.price + priceUpcharge;
            prices.userPrice = props.currentSku.userPrice + priceUserUpcharge;
            return;
        }

        prices.price = props.currentSku.price;
        prices.userPrice = props.currentSku.userPrice;
    }

    const unWatchSku = watch(
        () => props.currentSku,
        setPrice,
        {
            immediate: true
        }
    );

    const unWatchAugPrices = watch(
        () => props.augmentPrices,
        setPrice,
        {
            immediate: true,
            deep: true
        }
    );

    onBeforeUnmount(() => {
        unWatchAugPrices();
        unWatchSku();
    });
</script>

<style lang="scss"></style>
